// Voice lint — Wave C.
//
// Pure function. Returns an array of warnings (never blocks save). Wires into:
//   • Admin CMS editor (live-lint on textarea changes, debounced)
//   • Worker save-time (warnings included in audit_log.payload.lintWarnings)
//
// Rule set is derived from prd/02_voice.md. We intentionally stay narrow:
//   1. Forbidden performative interjections.
//   2. Glossary substitutions (use product-specific terms).
//   3. Style hints (excessive punctuation, emoji density).
//
// Severity is always 'warn' for now. If a rule needs blocking semantics,
// add a `severity` field and change the editor's submit guard.

export type LintWarning = {
  /** 1-based char offset for editor highlighting. */
  offset: number;
  /** Length of the matched span. */
  length: number;
  /** Stable rule id for telemetry. */
  rule: string;
  /** Short human-readable message. */
  message: string;
};

type Rule = {
  id: string;
  pattern: RegExp;
  message: (matched: string) => string;
};

const FORBIDDEN_TOKENS: Rule = {
  id: 'forbidden-interjection',
  pattern: /\b(Awesome|Oops|Sorry|Got it|Yay)\s*!/g,
  message: (m) => `Avoid "${m}". Performative interjections aren’t in the voice guide.`,
};

const GLOSSARY_SUBSTITUTIONS: ReadonlyArray<Rule> = [
  {
    id: 'glossary-booking',
    pattern: /\b(Booking|Bookings|Reservation|Reservations)\b/g,
    message: () => 'Use "exchange" instead of "booking" / "reservation".',
  },
  {
    id: 'glossary-login',
    pattern: /\b(Login|Logout)\b/g,
    message: (m) =>
      m === 'Login' ? 'Use "Sign in" instead of "Login".' : 'Use "Sign out" instead of "Logout".',
  },
  {
    id: 'glossary-property',
    pattern: /\b(Property|Listing)\b/gi,
    message: () => 'Use "host" or "venue" instead of "property" / "listing".',
  },
  {
    id: 'glossary-submit-booking',
    pattern: /\bSubmit\s+(?:a|an)\s+(application|booking|request)\b/gi,
    message: () => 'Use "send a request" rather than "submit a booking".',
  },
];

const EXCESSIVE_BANG: Rule = {
  id: 'style-excessive-bang',
  pattern: /!{3,}/g,
  message: (m) => `Avoid ${m.length} exclamation marks in a row. One is plenty.`,
};

const ALL_RULES: ReadonlyArray<Rule> = [FORBIDDEN_TOKENS, ...GLOSSARY_SUBSTITUTIONS, EXCESSIVE_BANG];

const EMOJI_REGEX = /\p{Extended_Pictographic}/gu;

/**
 * Run every rule against `text` and return matching warnings. Pure and
 * synchronous — safe to call on every keystroke (debounce in the UI).
 */
export function lintVoice(text: string): LintWarning[] {
  const warnings: LintWarning[] = [];
  for (const rule of ALL_RULES) {
    // RegExp objects with `g` flag are stateful; reset before reuse.
    rule.pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = rule.pattern.exec(text)) !== null) {
      warnings.push({
        offset: m.index + 1,
        length: m[0].length,
        rule: rule.id,
        message: rule.message(m[0]),
      });
      // Defensive: zero-width match would loop forever.
      if (m.index === rule.pattern.lastIndex) rule.pattern.lastIndex += 1;
    }
  }

  // Emoji density heuristic — > 1 emoji per 200 chars.
  if (text.length >= 50) {
    const emojiMatches = text.match(EMOJI_REGEX) ?? [];
    if (emojiMatches.length > Math.max(1, Math.floor(text.length / 200))) {
      warnings.push({
        offset: 1,
        length: 1,
        rule: 'style-emoji-density',
        message: `Heavy emoji use (${emojiMatches.length} in ${text.length} chars). Voice guide prefers sparing use.`,
      });
    }
  }

  return warnings;
}
